import "./List.scss";
import { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Typography,
  OverflowContainer,
  Skeleton,
  SecondaryButton,
  Icon
} from "@husqvarna/ui-core";
import TextButtonWithIcon from "../../shared/components/TextButtonWithIcon";
import { ReactComponent as FolderIcon } from "../../shared/icons/folder.svg";
import { ReactComponent as SortIcon } from "../../shared/icons/table-arrows.svg";
import ProjectOverview from '../models/ProjectOverview';
import { useCopyProjectMutation, useDeleteAccountLevelMutation, useDeleteProjectMutation, useGetAllProjectsQuery, useMoveProjectToAccountLevelMutation, useRemoveAccountLevelFromProjectMutation } from '../../project/projectApi';
import ProjectEditDialog from "../components/ProjectEditDialog";
import ProjectListItem from "../components/ProjectListItem";
import { SortOrder } from "../models/Enums";
import AccountLevelDialog from "../components/ProjectList/AccountLevelDialog/AccountLevelDialog";
import AccountLevel from "../models/AccountLevel";
import FeedbackToast from "../../shared/components/FeedbackToast";
import { useNavigate } from "react-router";
import AccountLevelItem from "../components/ProjectList/AccountLevelItem/AccountLevelItem";
import DashboardItem from "../models/DashboardItem";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, TouchSensor, DragOverlay, DragStartEvent, DragOverEvent, DragCancelEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";
import DraggableProjectListItem from "../components/ProjectList/DraggableProjectListItem/DraggableProjectListItem";
import clsx from "clsx";
import { sortDashboardItems, SortDirection } from "../../shared/utils";

const Dashboard = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [copyProject] = useCopyProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [deleteAccountLevel] = useDeleteAccountLevelMutation();
  const [moveProjectToAccountLevel] = useMoveProjectToAccountLevelMutation();
  const [removeAccountLevelFromProject] = useRemoveAccountLevelFromProjectMutation();
  const [addProjectIsOpen, setAddProjectIsOpen] = useState(false);
  const [addAccountLevelIsOpen, setAddAccountLevelIsOpen] = useState(false);
  const [scrollToTop, setScrollToTop] = useState(false);
  const { data: projectsFromQuery, isLoading } = useGetAllProjectsQuery();
  const [projects, setProjects] = useState<DashboardItem[] | undefined>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSuccess, setToastSuccess] = useState(true);
  const [selectedAccountLevel, setSelectedAccountLevel] = useState<AccountLevel | undefined>(undefined);
  const [selectedProject, setSelectedProject] = useState<ProjectOverview | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState(SortOrder.LatestChange);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const overflowContainerRef = useRef<HTMLDivElement>(null);
  const [activeDraggableProject, setActiveDraggableProject] = useState<ProjectOverview | undefined>(undefined);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      }}),
      useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10
    }}))
  
  const gotoProject = (projectId: string) => {
    navigate(`/projects/${projectId}/`);
  };

  useEffect(() => {
    const sortedProjects = sortDashboardItems(projectsFromQuery, sortOrder, sortDirection);
    setProjects(sortedProjects);
  }, [projectsFromQuery, sortOrder, sortDirection]);

  useEffect(() => {
    if (scrollToTop && overflowContainerRef.current) {
      overflowContainerRef.current.scrollTop = 0;
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  useEffect(() => {
    if (showToast) {
      setTimeout(() => {
        setShowToast(false);
      }, 3000)
    }
  }, [showToast]);

  const handleAccountLevelEdit = (accountLevel:AccountLevel) => {
    setSelectedAccountLevel(accountLevel);
    setAddAccountLevelIsOpen(true);
  }

  const handleDialogClose = (project: ProjectOverview | undefined) => {
    setAddProjectIsOpen(false);
    setSelectedProject(undefined);
    if (project && !selectedProject) {
      navigate(`/projects/${project.id}/`);
    }
  }

  const handleAccountLevelClose = () => {
    setAddAccountLevelIsOpen(false);
    setSelectedAccountLevel(undefined);
  }

  const handleEditProject = (project:ProjectOverview) => {
    setSelectedProject(project);
    setAddProjectIsOpen(true);
  }

  const handleCopyProject = (project: ProjectOverview) => {
    if (project) {
      copyProject(project.id).then(res => {
        if ("error" in res) {
          setToastSuccess(false);
          setToastMessage(intl.formatMessage({
            id: "projects.list.selectedProject.copyFailed",
            defaultMessage: "Your project could not be duplicated. Please try again.",
          }));
          setShowToast(true);
        }
        else if ("data" in res) {
          setToastSuccess(true);
          setToastMessage(intl.formatMessage({
            id: "projects.list.selectedProject.copySuccessful",
            defaultMessage: "Your project has been duplicated",
          }));
          setShowToast(true);
          setScrollToTop(true);
        }
      });
    }
  }

  const handleDeleteProject = (project: ProjectOverview) => {
    if (project) {
      deleteProject(project.id).then(res => {
        if ("error" in res) {
          setToastSuccess(false);
          setToastMessage(intl.formatMessage({
            id: "projects.list.selectedProject.deleteFailed",
            defaultMessage: "Your project could not be deleted. Please try again.",
          }));
        } else {
          setToastSuccess(true);
          setToastMessage(intl.formatMessage({
            id: "projects.list.selectedProject.deleteSuccessful",
            defaultMessage: "Your project has been deleted",
          }));

          setScrollToTop(true);
        }

        setShowToast(true);
      });
    }
  }

  const handleDeleteAccountLevel = (id: string) => {
    deleteAccountLevel(id).then(res => {
      if ("error" in res) {
        setToastSuccess(false);
        setToastMessage(intl.formatMessage({
          id: "projects.list.selectedAccountLevel.deleteFailed",
          defaultMessage: "Your account level could not be deleted. Please try again.",
        }));
      } else {
        setToastSuccess(true);
        setToastMessage(intl.formatMessage({
          id: "projects.list.selectedAccountLevel.deleteSuccessful",
          defaultMessage: "Your account level has been deleted.",
        }));

        setScrollToTop(true);
      }

      setShowToast(true);
    });
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const projectId = event.active.id.toString();
    if (event.over) {
      if (activeDraggableProject?.accountLevel?.id !== event.over.id.toString()) {
        moveProjectToAccountLevel({projectId: projectId, accountLevelId: event.over.id.toString()}).then(res => {
          if ("error" in res) {
            setToastSuccess(false);
            setToastMessage(intl.formatMessage({
              id: "projects.list.project.moveFailed",
              defaultMessage: "Your project could not be moved. Please try again.",
            }));
          } else {
            setToastSuccess(true);
            setToastMessage(intl.formatMessage({
              id: "projects.list.project.moveIntoSuccessful",
              defaultMessage: "Your project moved into the account level successfully.",
            }));
    
            setScrollToTop(true);
          }
    
          setShowToast(true);
        });
        setIsDraggedOver(false);
      }
    } else if (activeDraggableProject?.accountLevel != null) {
      removeAccountLevelFromProject(projectId).then(res => {
        if ("error" in res) {
          setToastSuccess(false);
          setToastMessage(intl.formatMessage({
            id: "projects.list.project.moveFailed",
            defaultMessage: "Your project could not be moved. Please try again.",
          }));
        } else {
          setToastSuccess(true);
          setToastMessage(intl.formatMessage({
            id: "projects.list.project.moveSuccessful",
            defaultMessage: "Your project moved successfully.",
          }));
  
          setScrollToTop(true);
        }
  
        setShowToast(true);
      });
    }
    setActiveDraggableProject(undefined);
  }

  const handleDragStart = (event: DragStartEvent) => {
    var proj = event.active.data?.current?.project ?? undefined;
    setActiveDraggableProject(proj);
  }

  const handleDragCancel = (event: DragCancelEvent) => {
    setActiveDraggableProject(undefined);
  }

  const handleDragOver = (event: DragOverEvent) => {
    if(event.over) {
      setIsDraggedOver(true);
    } else {
      setIsDraggedOver(false);
    }
  }

  const handleSortClick = (clickedOrder: SortOrder) => {
    if (clickedOrder === sortOrder) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortOrder(clickedOrder);
      setSortDirection("asc");
    }
  };

  const renderListItems = (item: DashboardItem) => {
    if (item.isGroup) {
      return (
        <AccountLevelItem
          key={item.id + Date.now}
          item={item}
          sortOrder={sortOrder}
          onProjectClick={(projectId) => gotoProject(projectId)}
          onEditProjectClick={(project) => handleEditProject(project)}
          onCopyProjectClick={(project) => handleCopyProject(project)}
          onDeleteProjectClick={(project) => handleDeleteProject(project)}
          onEdit={(id: string, name: string) => handleAccountLevelEdit({id, name})}
          onDelete={(id) => handleDeleteAccountLevel(id)} />
      );
    } else {
      const project = item.projects[0];
      if (!project) {
        return null;
      }
      return (
        <DraggableProjectListItem
          key={project.id + Date.now}
          project={project}
          onItemClick={() => gotoProject(project.id)}
          onEditItemClick={() => handleEditProject(project)}
          onDuplicateItemClick={() => handleCopyProject(project)}
          onDeleteItemClick={() => handleDeleteProject(project)} />
        );
    }
  }

  const listClasses = clsx('tcsp-dashboard__projects', {'tcsp-dashboard__projects-hover': !isDraggedOver && activeDraggableProject !== undefined && activeDraggableProject.accountLevel?.id !== undefined });

  return (
    <Box className="tcsp-dashboard" container width="full">
      <FeedbackToast show={showToast} success={toastSuccess} text={toastMessage}></FeedbackToast>
        <Box container flexGrow={1} ml={8} mr={12} pt={6} flexDirection="column" height="full">
          <Box flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="Heading3" tag="h1" mt={2} ml={12} mb={3}>
              <FormattedMessage id="dashboard.heading" defaultMessage="Your projects" />
            </Typography>
            <Box flexDirection="row" mb={2} mr={5}>
              <Box>
                <TextButtonWithIcon
                  icon={<FolderIcon />}
                  text={intl.formatMessage({ id: "general.createAccountLevelLinkText", defaultMessage: "New folder" })}
                  variant="secondary"
                  onClick={() => setAddAccountLevelIsOpen(!addAccountLevelIsOpen)}
                />
              </Box>
              <Box ml={2}>
                <TextButtonWithIcon
                  variant="primary"
                  icon={<Icon iconId="plus" size={4} />}
                  text={intl.formatMessage({ id: "general.createNew", defaultMessage: "Create new" })}
                  onClick={() => setAddProjectIsOpen(!addProjectIsOpen)}
                />
              </Box>
            </Box>
          </Box>
          <Box className="tcsp-dashboard__projects-header" mt={10} ml={8}>
            <Box>
              <TextButtonWithIcon
                variant="ghost"
                className={sortOrder === SortOrder.Name ? "active" : ""}
                icon={<SortIcon />} iconPosition="end" iconSize="small"
                text={intl.formatMessage({id:"dashboard.projectList.projectsTitle", defaultMessage: "Projects"})}
                onClick={() => handleSortClick(SortOrder.Name)} textVariant="Body1" textClassName="medium" />
            </Box>
            <Box>
              <TextButtonWithIcon
                variant="ghost"
                className={sortOrder === SortOrder.Status ? "active" : ""}
                icon={<SortIcon />} iconPosition="end" iconSize="small"
                text={intl.formatMessage({id:"dashboard.projectList.statusTitle", defaultMessage: "Status"})}
                onClick={() => handleSortClick(SortOrder.Status)} textVariant="Body1" textClassName="medium" />
            </Box>
            <Box>
              <TextButtonWithIcon
                variant="ghost"
                className={sortOrder === SortOrder.Type ? "active" : ""}
                icon={<SortIcon />} iconPosition="end" iconSize="small"
                text={intl.formatMessage({id:"dashboard.projectList.projectType", defaultMessage: "Type"})}
                onClick={() => handleSortClick(SortOrder.Type)} textVariant="Body1" textClassName="medium" />
            </Box>
            <Box>
              <TextButtonWithIcon
                variant="ghost"
                className={sortOrder === SortOrder.AreaCount ? "active" : ""}
                icon={<SortIcon />} iconPosition="end" iconSize="small"
                text={intl.formatMessage({id:"dashboard.projectList.areasTitle", defaultMessage: "Areas"})}
                onClick={() => handleSortClick(SortOrder.AreaCount)} textVariant="Body1" textClassName="medium" />
            </Box>
            <Box textAlign="right">
              <TextButtonWithIcon
                variant="ghost"
                className={sortOrder === SortOrder.TotalAreaSize ? "active" : ""}
                icon={<SortIcon />} iconPosition="end" iconSize="small"
                text={intl.formatMessage({id:"dashboard.projectList.sizeTitle", defaultMessage: "Size"})}
                onClick={() => handleSortClick(SortOrder.TotalAreaSize)} textVariant="Body1" textClassName="medium" />
            </Box>
            <Box>
              <TextButtonWithIcon
                variant="ghost"
                className={sortOrder === SortOrder.LatestChange ? "active" : ""}
                icon={<SortIcon />} iconPosition="end" iconSize="small"
                text={intl.formatMessage({id:"dashboard.projectList.latestChangedTitle", defaultMessage: "Latest changed"})}
                onClick={() => handleSortClick(SortOrder.LatestChange)} textVariant="Body1" textClassName="medium" />
            </Box>
          </Box>
          <OverflowContainer className="tcsp-dashboard__projects-list" ref={overflowContainerRef} direction="vertical" fadeOut="mask" offset={0} pt={5} pr={2} mb={2}>
            <DndContext onDragCancel={handleDragCancel} onDragOver={handleDragOver} onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors} modifiers={[restrictToVerticalAxis]}>
              <ol className={listClasses}>
                {isLoading && (
                  <>
                    {Array(5).fill(undefined).map((_, index) => (
                      <Box key={index} component="li" mb={2}>
                        <Skeleton ml={12} br={6} height="65px" />
                      </Box>
                    ))}
                  </>
                )}
                {!isLoading && projects?.map((project: DashboardItem) => (
                    renderListItems(project)
                ))}
              </ol>
              <DragOverlay dropAnimation={null} modifiers={[restrictToWindowEdges]}>
                {activeDraggableProject && (
                  <ProjectListItem project={activeDraggableProject} isDragging={true} isActive={false} isOver={isDraggedOver} />
                )}
              </DragOverlay>
            </DndContext>
            {!isLoading && !projects?.length && (
              <Box alignItems="center" mt={30} flexDirection="column" textAlign="center" flexGrow={1}>
                <Typography variant="Heading4" tag="h4">
                  <FormattedMessage id="projects.list.noProjectsHeading" defaultMessage="Create a project" />
                </Typography>
                <Typography variant="Body1" tag="p" mt={3}>
                  <FormattedMessage id="projects.list.noProjectsText" defaultMessage="You don't have any projects, create a project to get started!" />
                </Typography>
                <SecondaryButton
                  mt={8}
                  onClick={() => setAddProjectIsOpen(!addProjectIsOpen)}
                  text={intl.formatMessage({
                    id: "general.createProjectLinkText",
                    defaultMessage: "Project"
                  })} />
              </Box>
            )} 
          </OverflowContainer>
      </Box>
      <ProjectEditDialog
        project={selectedProject}
        isOpen={addProjectIsOpen}
        onClosing={(project) => handleDialogClose(project)} />
      <AccountLevelDialog
        accountLevel={selectedAccountLevel}
        isOpen={addAccountLevelIsOpen}
        onClosing={() => handleAccountLevelClose()} />
    </Box>
  );
}

export default Dashboard;
